// ============================================================================
// ForgotPasswordPage — route /forgot (PUBLIC, trainer realm, namespace "forgot").
// ----------------------------------------------------------------------------
// A trainer requests a password-reset link by email (POST /api/auth/forgot via
// authApi.forgot). The endpoint is NON-ENUMERATING: it always returns a generic
// success whether or not the email is registered, so this page ALWAYS shows the
// same "if that account exists, we sent a link" confirmation on success — it
// never reveals whether the address matched. A 429 (per-IP rate limit) is the
// only distinct outcome, surfaced as a calm "try again later" message.
//
// Mobile-first Pulse: a single centred card, reduced-motion-aware reveal, EN +
// 繁中 via i18n keys only. Reuses the locked Card / Button / TextField / Banner.
// ============================================================================
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { Banner, Button, Card, TextField } from "../components";
import { ApiError, authApi } from "../api";
import { LanguageToggle } from "../i18n/LanguageToggle";

const EASE = [0.22, 0.61, 0.36, 1] as const;

function BrandMark() {
  const { t } = useTranslation();
  return (
    <Link to="/" className="inline-flex items-center gap-2.5">
      <span className="grid h-9 w-9 place-items-center rounded-md bg-grad-coral shadow-volt">
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
          <path
            d="M3 13h4l2-6 3 11 2.6-8L18 13h3"
            stroke="#fff"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="font-display text-lg font-extrabold tracking-tightest text-body">
        {t("app.name", { ns: "common" })}
      </span>
    </Link>
  );
}

export function ForgotPasswordPage() {
  const { t, ready } = useTranslation("forgot");
  const reduce = useReducedMotion();

  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!ready) return <div className="min-h-screen bg-paper" aria-hidden />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await authApi.forgot({ email: email.trim() });
      // ALWAYS the same generic confirmation — never reveal if the email matched.
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError(t("rateLimited"));
      } else {
        setError(t("genericError"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-body">
      <header className="mx-auto flex max-w-md items-center justify-between px-5 py-5">
        <BrandMark />
        <LanguageToggle />
      </header>
      <main className="mx-auto flex max-w-md flex-col px-5 pb-16 pt-2">
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: EASE }}
        >
          {sent ? (
            <Card className="space-y-5">
              <span className="grid h-12 w-12 place-items-center rounded-md bg-lime-soft text-lime-600">
                <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden>
                  <path
                    d="M4 6.5 12 12l8-5.5M4 6h16v12H4z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <div>
                <h1 className="font-display text-2xl font-extrabold tracking-tightest text-body">
                  {t("sent.title")}
                </h1>
                <p className="mt-1.5 text-body-soft">{t("sent.blurb")}</p>
              </div>
              <p className="rounded-md bg-surface-sunken px-4 py-3 text-sm text-body-soft">
                {t("sent.hint")}
              </p>
              <Link to="/login">
                <Button block size="lg" variant="secondary">
                  {t("backToLogin")}
                </Button>
              </Link>
            </Card>
          ) : (
            <Card className="space-y-5">
              <div>
                <p className="font-display text-xs font-bold uppercase tracking-kicker text-coral">
                  {t("kicker")}
                </p>
                <h1 className="mt-1 font-display text-2xl font-extrabold tracking-tightest text-body">
                  {t("title")}
                </h1>
                <p className="mt-1.5 text-body-soft">{t("blurb")}</p>
              </div>

              {error && <Banner tone="danger" title={error} />}

              <form className="space-y-4" onSubmit={onSubmit} noValidate>
                <TextField
                  label={t("email")}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder={t("emailPlaceholder")}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError(null);
                  }}
                  required
                  autoFocus
                  disabled={submitting}
                />
                <Button type="submit" block size="lg" disabled={submitting}>
                  {submitting ? t("sending") : t("submit")}
                </Button>
              </form>

              <p className="text-center text-sm text-body-soft">
                {t("remembered")}{" "}
                <Link
                  to="/login"
                  className="font-bold text-coral transition-colors hover:text-volt-600"
                >
                  {t("backToLogin")}
                </Link>
              </p>
            </Card>
          )}
        </motion.div>
      </main>
    </div>
  );
}
